// AuthProvider.tsx — wires API tokens for the active auth mode. With Entra
// configured the (lazily loaded) MSAL provider takes over; otherwise the dev
// JWT from localStorage feeds the API client and no MSAL code is downloaded.

import { Suspense, lazy, useEffect } from 'react';
import type { ReactNode } from 'react';
import { authEnabled } from './msalConfig';
import { setTokenProvider } from '@/api/client';
import { getFreshAccessToken } from './devAuth';

const MsalAuthProvider = lazy(() => import('./msalRuntime'));

// Wire the dev token provider eagerly at module load — before any component mounts and fires its
// first query. A useEffect runs too late: React flushes child effects before the parent's, so the
// app-shell's mount-time queries (me / alerts / notifications) would otherwise go out unauthenticated
// and bounce through a 401→refresh→retry on every cold load. getFreshAccessToken also refreshes an
// expired token up front. The MSAL runtime overrides this from its own provider once lazily loaded.
if (!authEnabled) {
  setTokenProvider(() => getFreshAccessToken());
}

function DevTokenBridge({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Re-affirm after mount (covers HMR / a provider reset by the MSAL path being toggled off).
    setTokenProvider(() => getFreshAccessToken());
  }, []);
  return <>{children}</>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (!authEnabled) {
    // Auth disabled — dev JWT against the real API, or unauthenticated mocks.
    return <DevTokenBridge>{children}</DevTokenBridge>;
  }
  return (
    <Suspense fallback={null}>
      <MsalAuthProvider>{children}</MsalAuthProvider>
    </Suspense>
  );
}
